export function entityId(value) {
    const entity = value?.data || value;
    return entity?.documentId ?? entity?.id ?? entity ?? null;
}

export function entityLabel(value) {
    const entity = value?.data || value;
    if (!entity) return "-";
    if (typeof entity === "string" || typeof entity === "number") return String(entity);
    return entity.name
        || entity.title
        || entity.fullname
        || entity.username
        || entity.email
        || entity.documentId
        || entity.id
        || "-";
}

export function relationItems(value) {
    const relation = value?.data ?? value?.results ?? value;
    if (Array.isArray(relation)) return relation;
    return relation ? [relation] : [];
}

export function workspaceIdOf(entity) {
    return entityId(entity?.workspace);
}

export function membershipIdOf(role) {
    return entityId(role?.workspace_membership);
}

export function groupBy(items, keySelector) {
    return (items || []).reduce((groups, item) => {
        const key = keySelector(item);
        if (key == null) return groups;
        const normalizedKey = String(key);
        if (!groups.has(normalizedKey)) groups.set(normalizedKey, []);
        groups.get(normalizedKey).push(item);
        return groups;
    }, new Map());
}

export function isCurrentSubscription(subscription, now = Date.now()) {
    const activeStatuses = new Set(["active", "trialing"]);
    if (!activeStatuses.has(subscription?.subscriptionStatus)) return false;
    const end = subscription?.currentPeriodEnd || subscription?.endedAt || subscription?.expireAt;
    return !end || new Date(end).getTime() >= now;
}

export function buildWorkspaceHealth({
    workspaces = [],
    memberships = [],
    roles = [],
    subscriptions = [],
    wallets = [],
    aiSettings = [],
}) {
    const membershipsAvailable = Array.isArray(memberships);
    const rolesAvailable = Array.isArray(roles);
    const subscriptionsAvailable = Array.isArray(subscriptions);
    const walletsAvailable = Array.isArray(wallets);
    const aiSettingsAvailable = Array.isArray(aiSettings);
    const membershipsByWorkspace = groupBy(memberships, workspaceIdOf);
    const rolesByMembership = groupBy(roles, membershipIdOf);
    const subscriptionsByWorkspace = groupBy(subscriptions, workspaceIdOf);
    const walletsByWorkspace = groupBy(wallets, workspaceIdOf);
    const aiSettingsByWorkspace = groupBy(aiSettings, workspaceIdOf);

    return workspaces.map((workspace) => {
        const workspaceId = String(entityId(workspace));
        const workspaceMemberships = membershipsByWorkspace.get(workspaceId) || [];
        const joinedMemberships = workspaceMemberships.filter(
            (membership) => membership.memberShipStatus === "joined"
        );
        const issues = [];

        if (!entityId(workspace.owner)) {
            issues.push({code: "missing-owner", severity: "critical"});
        }
        if (membershipsAvailable && joinedMemberships.length === 0) {
            issues.push({code: "missing-membership", severity: "critical"});
        }

        if (membershipsAvailable) workspaceMemberships.forEach((membership) => {
            if (!entityId(membership.member)) {
                issues.push({
                    code: "membership-without-member",
                    severity: "critical",
                    entity: membership,
                });
            }
            if (membership.memberShipStatus !== "joined" || !rolesAvailable) return;
            const membershipRoles = (rolesByMembership.get(String(entityId(membership))) || [])
                .filter((role) => role.scope_type === "workspace");
            if (membershipRoles.length === 0) {
                issues.push({
                    code: "membership-without-role",
                    severity: "critical",
                    entity: membership,
                });
            }
            if (membershipRoles.length > 1) {
                issues.push({
                    code: "duplicate-workspace-role",
                    severity: "critical",
                    entity: membership,
                    roles: membershipRoles,
                });
            }
        });

        const currentSubscriptions = (subscriptionsByWorkspace.get(workspaceId) || [])
            .filter((subscription) => isCurrentSubscription(subscription));
        if (subscriptionsAvailable && currentSubscriptions.length === 0) {
            issues.push({code: "missing-subscription", severity: "warning"});
        }
        if (walletsAvailable && (walletsByWorkspace.get(workspaceId) || []).length === 0) {
            issues.push({code: "missing-wallet", severity: "warning"});
        }
        const aiEnabled = workspace.ai_enabled !== false;
        if (
            aiSettingsAvailable
            && aiEnabled
            && (aiSettingsByWorkspace.get(workspaceId) || []).length === 0
        ) {
            issues.push({code: "missing-ai-setting", severity: "warning"});
        }

        const criticalCount = issues.filter((issue) => issue.severity === "critical").length;
        const warningCount = issues.length - criticalCount;
        const score = Math.max(0, 100 - criticalCount * 25 - warningCount * 10);
        const health = criticalCount > 0 ? "critical" : warningCount > 0 ? "warning" : "healthy";

        return {
            ...workspace,
            _key: entityId(workspace),
            _health: health,
            _healthScore: score,
            _issues: issues,
            _memberships: workspaceMemberships.length,
            _currentSubscriptions: currentSubscriptions.length,
        };
    });
}

export function buildFinancialDiagnostics({
    orders = [],
    invoices = [],
    payments = [],
    subscriptions = [],
    manualPayments = [],
}) {
    const ordersAvailable = Array.isArray(orders);
    const invoicesAvailable = Array.isArray(invoices);
    const paymentsAvailable = Array.isArray(payments);
    const subscriptionsAvailable = Array.isArray(subscriptions);
    const manualPaymentsAvailable = Array.isArray(manualPayments);
    const invoicesById = new Map((invoices || []).map(
        (invoice) => [String(entityId(invoice)), invoice]
    ));
    const successfulPaymentsByInvoice = groupBy(
        (payments || []).filter(
            (payment) => ["succeeded", "completed"].includes(payment.paymentStatus)
        ),
        (payment) => entityId(payment.invoice)
    );
    const issues = [];

    if (paymentsAvailable && invoicesAvailable) payments.forEach((payment) => {
        if (!["succeeded", "completed"].includes(payment.paymentStatus)) return;
        const invoiceId = entityId(payment.invoice);
        const invoice = invoiceId != null ? invoicesById.get(String(invoiceId)) : null;
        if (invoice && invoice.invoiceStatus !== "paid") {
            issues.push({
                code: "payment-succeeded-invoice-unpaid",
                severity: "critical",
                workspace: payment.workspace,
                reference: payment.transactionNumber || payment.providerReference || entityId(payment),
                entity: payment,
            });
        }
    });

    if (invoicesAvailable && paymentsAvailable) invoices.forEach((invoice) => {
        if (invoice.invoiceStatus !== "paid") return;
        const successfulPayments = successfulPaymentsByInvoice.get(String(entityId(invoice))) || [];
        if (successfulPayments.length === 0) {
            issues.push({
                code: "invoice-paid-without-payment",
                severity: "critical",
                workspace: invoice.workspace,
                reference: invoice.invoiceNumber || entityId(invoice),
                entity: invoice,
            });
        }
    });

    if (ordersAvailable && invoicesAvailable) orders.forEach((order) => {
        if (order.orderStatus !== "paid") return;
        const hasInvoice = invoices.some(
            (invoice) => String(entityId(invoice.order)) === String(entityId(order))
        );
        if (!hasInvoice) {
            issues.push({
                code: "paid-order-without-invoice",
                severity: "warning",
                workspace: order.workspace,
                reference: order.orderNumber || entityId(order),
                entity: order,
            });
        }
    });

    if (subscriptionsAvailable) subscriptions.forEach((subscription) => {
        const recurringAmount = Number(subscription.recurringAmount || 0);
        if (isCurrentSubscription(subscription) && recurringAmount > 0 && !entityId(subscription.plan)) {
            issues.push({
                code: "paid-subscription-without-plan",
                severity: "warning",
                workspace: subscription.workspace,
                reference: subscription.subscriptionNumber || entityId(subscription),
                entity: subscription,
            });
        }
    });

    if (manualPaymentsAvailable) manualPayments.forEach((request) => {
        if (request.manualPaymentRequestStatus === "approved" && !entityId(request.order)) {
            issues.push({
                code: "approved-manual-payment-without-order",
                severity: "warning",
                workspace: request.workspace,
                reference: request.requestNumber || entityId(request),
                entity: request,
            });
        }
    });

    return issues;
}

export function statusCount(items, field, statuses) {
    const accepted = new Set(Array.isArray(statuses) ? statuses : [statuses]);
    return (items || []).filter((item) => accepted.has(item?.[field])).length;
}

export function formatMoney(value) {
    const amount = Number(value || 0);
    return Number.isFinite(amount) ? new Intl.NumberFormat("en-US").format(amount) : "-";
}
